import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../authentication/AuthContext";

// Fetches the assignable roles from the database.
//
// Replaces the hardcoded [user, reseller, sales, admin] dropdowns that used to
// be duplicated in the user create and edit forms. Those could not show a role
// added through the Roles screen, which would have made new roles impossible to
// actually assign to anyone.
//
// Returns roles as react-select options, each carrying the role's user_type so
// the form can react to it — the reseller-code field, for instance, only applies
// to customer-portal roles.
//
//   const { roles, roleOptions, loading, error } = useRoles();
//
// Reads GET /admin/roles/dropdown (admin.roles.dropdown) — the bare id/name
// list, grantable on its own so a role like sales gets role pickers and filters
// without the Roles tab. A caller holding only admin.roles.index (a custom role
// set up before the dropdown key existed) falls back to the full list, so no one
// loses a dropdown they had. A caller with neither gets an empty list and no
// request at all — callers should hide whatever control needed the list rather
// than render it empty.
//
// Pass { enabled: false } to skip the request entirely, for screens where the
// caller is known not to need (or not to be allowed) the role list — a reseller
// viewing the shared users table, for instance. Without it every such page load
// fires a request that can only 403.
export function useRoles({ enabled = true } = {}) {
  const { can } = useAuth();
  const endpoint = can("admin.roles.dropdown")
    ? "/admin/roles/dropdown"
    : can("admin.roles.index")
      ? "/admin/roles"
      : null;
  const active = enabled && !!endpoint;

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(active);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!active) {
      setRoles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        headers: { "access-token": localStorage.getItem("accessToken") },
      })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.status) {
          setRoles(res.data.roles || []);
        } else {
          setError(res.data?.message || "Could not load roles");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // 403 just means this user cannot manage roles; leave the list empty
        // rather than shouting about it.
        if (err.response?.status !== 403) {
          console.error("Failed to load roles:", err);
          setError("Could not load roles");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [active, endpoint]);

  const roleOptions = roles.map((r) => ({
    value: r.id,
    label: r.name,
    userType: r.user_type,
    slug: r.slug,
  }));

  return { roles, roleOptions, loading, error };
}
